import Config from "./Config";
import BaseService from './BaseService';
import AppStorage from "../storage/AppStorage";
import RestService from "./RestService";
import type { AppUpdatesData } from "./RestService";
import RefreshTimer from "../data/RefreshTimer";
import semver from "semver/preload";
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {CityService} from "./CityService";

/**
 * Helper for App updates, specialy for PWA.
 */
class UpdateService extends BaseService {
   /**
    * Define storage class
    */
    static storage = AppStorage;

    static refreshTimer = new RefreshTimer();

    /**
     * Gets debug information.
     */
    static getDebugInfo(): string {
      const lastChecked = this.refreshTimer.formatLast();
      return lastChecked ? "Last check " + lastChecked : '';
    }

    /**
     * Check current App version vs installed one.
     *
     * @return true if not set or it matches.
     */
    static checkRunningAppVersion(): boolean {
      const runningVersion = Config.getAppVersion();
      const installedVersion = this.storage.getInstalledVersion();

      if (!installedVersion) {
        this.info('Setting installed version', runningVersion);
        this.storage.setInstalledVersion(runningVersion);
        this.resetCityList();
        return true;
      }
      else if (installedVersion === runningVersion) {
        this.debug("App version, ok", runningVersion);
        return true;
      } else {
        this.debug("Setting new App version", runningVersion);
        this.storage.setInstalledVersion(runningVersion);
        this.resetCityList();
        return false;
      }
    }

    /**
     * Drop the stored city list so it is downloaded again for this version.
     *
     * Fire and forget: it is a refresh of the data feeding the city search, so
     * a failure must not surface as an unhandled rejection at startup.
     */
    static resetCityList(): void {
      CityService.resetCityList().catch((error) => {
        this.logger().warn('City list not reset.', error);
      });
    }

    /**
     * Check App version against remotely reported one.
     *
     * @param current Version to check against (m.n.p).
     *
     * @return La informacion de la actualizacion si la version publicada es
     *   mayor que la que corre; undefined en cualquier otro caso.
     */
    static async checkServerAppVersion(current: string): Promise<AppUpdatesData | undefined> {
      const updates = await RestService.getAppUpdates();
      if (updates && semver.gt(updates.app_version, current)) {
        return updates;
      }
    }

    /**
     * Version instalada con la que comparar la publicada.
     *
     * En nativo es la del paquete (CFBundleShortVersionString en iOS,
     * versionName en Android): es la que numera la tienda y la que el usuario
     * tiene instalada. NO se usa la del bundle JS (package.json, via
     * Config.getAppVersion): son numeraciones independientes y en cuanto se
     * separan la comparacion avisaria de una actualizacion ya instalada, o
     * callaria habiendo una.
     */
    static async getRunningVersion(): Promise<string> {
      if (!Capacitor.isNativePlatform()) {
        return Config.getAppVersion();
      }

      try {
        return (await App.getInfo()).version;
      } catch (error) {
        this.logger().warn('Native app version not available.', error);
        return Config.getAppVersion();
      }
    }

    /**
     * Comprueba si el backend publica una version mayor que la instalada.
     *
     * Se pregunta en cada arranque y en cada vuelta a primer plano, sin
     * cachear: la respuesta cambia justo cuando se publica una version, y es
     * una peticion pequena y sin autenticacion. No rechaza nunca: un fallo, o
     * estar sin red, es simplemente "no hay actualizacion".
     */
    static async checkAppUpdate(): Promise<AppUpdatesData | false> {
      try {
        const updates = await this.checkServerAppVersion(await this.getRunningVersion());
        return updates ?? false;
      } catch (error) {
        this.logger().warn('App update not checked.', error);
        return false;
      }
    }

    /**
     * Whether the update notice is still pending for a published version.
     *
     * Se avisa una sola vez por version publicada: si mas adelante sale otra
     * mayor, se vuelve a avisar. Lo que se guarda es la version avisada, no un
     * booleano, precisamente para poder distinguir esos dos casos.
     *
     * @param version Version publicada, la del backend.
     */
    static shouldNoticeUpdate(version: string): boolean {
      const noticed = this.storage.getUpdateNoticeVersion();

      // Sin avisar nunca, o con un valor que no es una version: se avisa.
      if (!noticed || !semver.valid(noticed)) {
        return true;
      }

      return semver.gt(version, noticed);
    }

    /**
     * Marks a published version as already noticed, so it is not repeated.
     *
     * @param version Version publicada, la del backend.
     */
    static setUpdateNoticed(version: string): void {
      this.info('App update noticed', version);
      this.storage.setUpdateNoticeVersion(version);
    }

    /**
     * Force app update to new version.
     *
     * @param updates Update information with app_version.
     *   If the parameter is empty, just force clean reload.
     */
    static forceUpdate(updates?: AppUpdatesData): void {
      if (updates) {
         window.alert("Gronze Maps Update, version " + updates.app_version);
      }
      else {
         window.alert("Reloading Gronze Maps");
      }
      // La recarga espera a que terminen la limpieza de cache y el desregistro
      // del service worker. El .finally garantiza que se recarga aunque alguna
      // de las dos falle.
      this.forceCacheClean()
          .then(() => this.forceSwUnregister())
          .finally(() => window.location.reload());
    }

    /**
     * Force service worker cache clean.
     */
    static async forceCacheClean(): Promise<void> {
        const cacheNames = await caches.keys();
        // Step through each cache name and delete it
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
    }

    /**
     * Unregister all service workers.
     */
    static async forceSwUnregister(): Promise<void> {
      if(window.navigator && navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for(const registration of registrations) {
          await registration.unregister();
        }
      }
    }

    /**
     * Initialize, from AppService.
     */
    static init(): void {
       // Initialize refresh timer.
       this.refreshTimer.setRefreshTime(Config.getRefreshTime());

       if (!this.checkRunningAppVersion()) {
           this.info("App needs update, new version", Config.getAppVersion());
           // Do not reload here, sw + index.js will take care.
           // window.location.reload(true);
       }
    }

    /**
     * Send app to OS store.
     *
     * @param isIos whether current native platform is iOS.
     *
     * On iOS, pass the App Store appId when available in config.
     */
    static async goToStore(isIos = false): Promise<void> {
      if (!isIos) {
        // OJO: aqui se pasaba { androidPackageName: 'com.gronze.maps' } para que
        // las builds de debug no apuntasen al paquete *.debug. El plugin no
        // admite esa opcion —OpenAppStoreOptions solo declara appId— y su
        // implementacion Android usa getContext().getPackageName(), asi que se
        // ignoraba por completo. Se quita porque era inerte, pero el problema
        // que pretendia resolver sigue sin resolverse.
        await AppUpdate.openAppStore();
        return;
      }

      const appStoreId = Config.getIosAppStoreId();
      if (appStoreId) {
        await AppUpdate.openAppStore({ appId: appStoreId });
        return;
      }

      await AppUpdate.openAppStore();
    }
}

export default UpdateService;
